// eslint-disable-next-line import/namespace, import/no-named-as-default-member, import/no-named-as-default, import/default
import screenfull from "screenfull"
import styled from "styled-components"
import { motion } from "framer-motion"

import LanguageSwitch from "./LanguageSwitch"
import useContent from "../hooks/useContent"
import { SCENE_MODE } from "../../shared/constants"
import { IconNext } from "./icons"
import { useEffect } from "react"

const StyledAnimationContainer = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`

const IntroOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh; /* fallback for Js load */
  height: var(--doc-height);
  color: white;
  background-color: rgba(0, 0, 0, 0.8);
  background-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 50%,
    rgba(0, 0, 0, 0.6) 100%
  );
`

const Image = styled(motion.img)`
  width: 100%;
  height: 100%;
  display: inline;
  object-fit: contain;
  box-sizing: border-box;
`

const Link = styled.a`
  color: white;
  text-decoration: none;
`

const Start = styled.div`
  position: absolute;
  top: 12%;
  width: 100%;
  height: 75%;
`

const Wrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  width: 100vw;
  height: 2.5rem;
  display: flex;
  padding: 0 12px;
  box-sizing: border-box;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
  background: rgba(54, 54, 54, 0.44);
  span:first-of-type {
    flex-grow: 1;
    padding: 0;
  }
  > * + * {
    margin-left: 20px;
    flex-grow: 0;
  }
`

const Button = styled(motion.button)`
  background-color: #363636;
  border: none;
  color: white;
  padding: 0.7rem 2.2rem;
  padding: 8px 16px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 1.48rem;
  font-weight: 700;
  border-radius: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
`

const Headline = styled(motion.h1)`
  font-size: 2rem;
  line-height: 2.75rem;
  font-weight: 700;
  margin: 0 12px;
  z-index: 5;
`

const StyledIconNext = styled(IconNext)`
  color: #ffcb0a;
  margin-left: 8px;
`

const StyledSpan = styled.span`
  margin-left: 24px;
`

const ImageWrapper = styled(motion.div)`
  position: absolute;
  top: 10%;
  width: 80%;
  height: 80%;
  display: flex;
  justify-content: center;
`

const Circle = styled(motion.div)`
  position: absolute;
  width: min(100vw, 100vh);
  height: min(100vw, 100vh);
  background: white;
  border-radius: 50%;
  z-index: -5;
  margin: 0 auto;
  left: 50%;
  top: 50%;
`

const animations = {
  variants: {
    container: {
      enter: {
        opacity: 1,
        backdropFilter: "blur(10px)",
        transition: {
          duration: 0.8,
          staggerChildren: 0.3,
        },
      },
      initial: {
        opacity: 0,
        backdropFilter: "blur(0px)",
      },
      exit: {
        backdropFilter: "blur(0px)",
        opacity: 0,
        transition: {
          duration: 0.8,
          staggerChildren: 0.1,
        },
      },
    },

    item: {
      enter: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3 },
      },
      initial: {
        opacity: 0,
        y: 30,
        transition: { duration: 0 },
      },
      exit: {
        opacity: 0,
        y: -30,
        transition: { duration: 0.3 },
      },
    },

    arrow: {
      enter: {
        x: [-3, 1, -3],
        transition: {
          repeat: Infinity,
          duration: 3,
          ease: "easeInOut",
          delay: 1,
        },
      },
      initial: {
        x: 0,
      },
    },

    circleList: {
      initial: {
        opacity: 0,
        y: 30,
        transition: { duration: 0 },
      },
      enter: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, staggerChildren: 2 },
      },
      exit: {
        opacity: 0,
        y: -30,
        transition: { duration: 0.3 },
      },
    },

    pulseCircleAnimation: {
      enter: {
        scale: [0.2, 0.9],
        x: "-50%",
        y: "-50%",
        opacity: [0, 0.05, 0.1, 0],
        transition: {
          repeat: Infinity,
          duration: 6,
          ease: [0.36, 0.11, 0.89, 0.32],
        },
      },
      initial: {
        x: "-50%",
        y: "-50%",

        opacity: 0,
      },
    },
  },

  initial: "initial",
  animate: "enter",
  exit: "exit",
}

const IntroScreen = ({ setAppState }) => {
  const content = useContent({})
  const { intro, privacy, imprint } = content

  useEffect(() => {
    if (screenfull.isEnabled) {
      screenfull.exit()
    }
  }, [])

  return (
    <StyledAnimationContainer
      variants={animations.variants.container}
      initial={animations.initial}
      animate={animations.animate}
      exit={animations.exit}
    >
      <IntroOverlay>
        <LanguageSwitch whileTap={{ scale: 0.9 }} />

        <Start>
          <Wrapper>
            <Headline variants={animations.variants.item}> {intro.headline} </Headline>
            <ImageWrapper variants={animations.variants.circleList}>
              <div style={{ position: "relative", width: "100%", heigth: "100%" }}>
                <Image src={intro.image} />
                <Circle variants={animations.variants.pulseCircleAnimation} />
                <Circle variants={animations.variants.pulseCircleAnimation} />
                <Circle variants={animations.variants.pulseCircleAnimation} />
              </div>
            </ImageWrapper>
            <Button
              variants={animations.variants.item}
              whileTap={{ scale: 0.9 }}
              onClick={() => setAppState(SCENE_MODE)}
            >
              <StyledSpan>{intro.start}</StyledSpan>
              <StyledIconNext size={40} variants={animations.variants.arrow} />
            </Button>
          </Wrapper>
        </Start>

        <Footer variants={animations.variants.item}>
          <span>© Stiftung Neanderthal Museum</span>
          <Link target="_blank" href={privacy.url}>
            {privacy.name}
          </Link>
          <Link target="_blank" href={imprint.url}>
            {imprint.name}
          </Link>
        </Footer>
      </IntroOverlay>
    </StyledAnimationContainer>
  )
}

export default IntroScreen
